import React, { useRef, useState, useEffect } from "react";
import ReactPlayer from "react-player";
import { makeStyles } from "@material-ui/core/styles";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import VolumeOffIcon from "@material-ui/icons/VolumeOff";
import FullscreenIcon from "@material-ui/icons/Fullscreen";

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const useStyles = makeStyles(() => ({
  wrapper: {
    position: "relative",
    paddingTop: "56.25%",
    backgroundColor: "rgb(16,16,16)",
  },
  iframeBlocker: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    cursor: "pointer",
  },
  watermarkCover: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 48,
    backgroundColor: "#000",
    zIndex: 2,
  },
  controls: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: "20px 12px 8px",
    background: "linear-gradient(transparent, rgba(0,0,0,0.75))",
    zIndex: 3,
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  controlsVisible: {
    opacity: 1,
  },
  seekBar: {
    display: "block",
    width: "100%",
    height: 4,
    marginBottom: 8,
    cursor: "pointer",
    accentColor: "#fff",
    backgroundColor: "transparent",
  },
  controlsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  controlsLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  controlsRight: {
    display: "flex",
    alignItems: "center",
  },
  controlBtn: {
    background: "none",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    "&:hover": { opacity: 0.8 },
  },
  volumeBar: {
    width: 70,
    height: 4,
    cursor: "pointer",
    accentColor: "#fff",
  },
  timeDisplay: {
    color: "#fff",
    fontSize: 13,
    whiteSpace: "nowrap",
    userSelect: "none",
  },
  speedMenuWrapper: {
    position: "relative",
    display: "inline-flex",
    marginLeft: 4,
  },
  speedMenuTrigger: {
    background: "none",
    border: "1px solid rgba(255,255,255,0.5)",
    color: "#fff",
    cursor: "pointer",
    padding: "2px 8px",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 3,
    "&:hover": { borderColor: "#fff" },
  },
  speedMenu: {
    position: "absolute",
    bottom: "calc(100% + 6px)",
    right: 0,
    background: "rgba(28,28,28,0.97)",
    borderRadius: 4,
    overflow: "hidden",
    minWidth: 72,
    boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
    zIndex: 10,
  },
  speedMenuItem: {
    color: "#fff",
    fontSize: 13,
    fontWeight: 500,
    padding: "7px 14px",
    cursor: "pointer",
    textAlign: "center",
    "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" },
  },
  speedMenuItemActive: {
    fontWeight: 700,
    backgroundColor: "rgba(255,255,255,0.08)",
  },
}));

const SPEEDS = [1, 1.25, 1.5, 2, 3];

const YoutubePlayer = ({ url, onEnded, onProgress, autoPlay = true, playing: playingProp, onPlay, onPause }) => {
  const classes = useStyles();
  const playerRef = useRef(null);
  const wrapperRef = useRef(null);
  const hideTimer = useRef(null);
  const pendingSeek = useRef(null);
  const speedMenuRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(playingProp !== undefined ? playingProp : autoPlay);

  useEffect(() => {
    if (playingProp !== undefined) setIsPlaying(playingProp);
  }, [playingProp]);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(true);
  const [seeking, setSeeking] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false);

  useEffect(() => {
    if (!speedMenuOpen) return;
    const handler = (e) => {
      if (speedMenuRef.current && !speedMenuRef.current.contains(e.target))
        setSpeedMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [speedMenuOpen]);

  const resetHideTimer = () => {
    setControlsVisible(true);
    clearTimeout(hideTimer.current);
    if (isPlaying) {
      hideTimer.current = setTimeout(() => setControlsVisible(false), 3000);
    }
  };

  const handleSeekMouseDown = () => setSeeking(true);

  const handleSeekChange = (e) => {
    const val = parseFloat(e.target.value);
    setPlayed(val);
    pendingSeek.current = val;
    playerRef.current.seekTo(val, "fraction");
  };

  const handleSeekMouseUp = () => {
    setSeeking(false);
    if (pendingSeek.current !== null) {
      playerRef.current.seekTo(pendingSeek.current, "fraction");
      pendingSeek.current = null;
    }
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    setIsMuted(val === 0);
  };

  const handleFullscreen = () => {
    if (!document.fullscreenElement) wrapperRef.current.requestFullscreen();
    else document.exitFullscreen();
  };

  const handleProgress = (state) => {
    if (!seeking) setPlayed(state.played);
    onProgress?.(state);
  };

  return (
    <div
      ref={wrapperRef}
      className={classes.wrapper}
      onMouseMove={resetHideTimer}
      onMouseEnter={resetHideTimer}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        className={classes.iframeBlocker}
        onClick={() => setIsPlaying((p) => !p)}
      />
      <div className={classes.watermarkCover} />
      <ReactPlayer
        ref={playerRef}
        url={url}
        playing={isPlaying}
        muted={isMuted}
        volume={volume}
        playbackRate={playbackRate}
        className="reactPlayerVideo"
        controls={false}
        pip={false}
        width="100%"
        height="100%"
        config={{ youtube: { playerVars: { modestbranding: 1, rel: 0 } } }}
        onContextMenu={(e) => e.preventDefault()}
        onProgress={handleProgress}
        onDuration={setDuration}
        onPlay={() => { setIsPlaying(true); onPlay?.(); }}
        onPause={() => {
          setIsPlaying(false);
          setControlsVisible(true);
          clearTimeout(hideTimer.current);
          onPause?.();
        }}
        onEnded={onEnded}
      />
      <div
        className={`${classes.controls} ${
          controlsVisible ? classes.controlsVisible : ""
        }`}
      >
        <input
          type="range"
          min={0}
          max={1}
          step={0.001}
          value={played}
          className={classes.seekBar}
          onMouseDown={handleSeekMouseDown}
          onChange={handleSeekChange}
          onMouseUp={handleSeekMouseUp}
        />
        <div className={classes.controlsRow}>
          <div className={classes.controlsLeft}>
            <button
              onClick={() => setIsPlaying((p) => !p)}
              className={classes.controlBtn}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </button>
            <button
              onClick={() => setIsMuted((m) => !m)}
              className={classes.controlBtn}
            >
              {isMuted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className={classes.volumeBar}
            />
            <span className={classes.timeDisplay}>
              {formatTime(played * duration)} / {formatTime(duration)}
            </span>
          </div>
          <div className={classes.controlsRight}>
            <div ref={speedMenuRef} className={classes.speedMenuWrapper}>
              <button
                className={classes.speedMenuTrigger}
                onClick={() => setSpeedMenuOpen((o) => !o)}
              >
                {playbackRate}x
              </button>
              {speedMenuOpen && (
                <div className={classes.speedMenu}>
                  {SPEEDS.map((rate) => (
                    <div
                      key={rate}
                      className={`${classes.speedMenuItem} ${
                        playbackRate === rate ? classes.speedMenuItemActive : ""
                      }`}
                      onClick={() => {
                        setPlaybackRate(rate);
                        setSpeedMenuOpen(false);
                      }}
                    >
                      {rate}x
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={handleFullscreen} className={classes.controlBtn}>
              <FullscreenIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YoutubePlayer;
